import { useState } from "react";
import { Button, Modal } from "@panelgenie/shared-ui";
import { copyTextToClipboard, type CopyTextToClipboard } from "../lib/clipboard.js";

/**
 * A large, read-only viewer for the raw model response JSON. Opened from
 * the detail view's "View JSON" action. The formatted text scrolls inside
 * a monospace block; the footer Copy button drops the same text on the
 * clipboard (with a transient "Copied ✓" confirmation, mirroring the old
 * inline copy affordance this modal replaced).
 *
 * `copyText` is injected so the copy path is unit-testable without a real
 * `navigator.clipboard`; it defaults to the production helper.
 */
export function JsonViewModal({
  json,
  onClose,
  copyText = copyTextToClipboard,
}: {
  readonly json: string;
  readonly onClose: () => void;
  readonly copyText?: CopyTextToClipboard;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void copyText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      aria-label="json-view"
      title="Raw model response"
      className="max-w-5xl"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={handleCopy} aria-label="copy-json">
            {copied ? "Copied ✓" : "Copy"}
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <pre className="max-h-[70vh] overflow-auto rounded-lg border border-line bg-bg-1 p-4 font-mono text-xs leading-relaxed text-fg-1">
        {json}
      </pre>
    </Modal>
  );
}
